import { type CSSProperties } from 'react';

import type { Tokens } from '@zeus/themes';

import { alpha, type ThemeOptions } from '@mui/material';

export function createButtonStyles(tokens: Tokens) {
  return {
    MuiButton: {
      defaultProps: {
        disableRipple: true,
      },
      styleOverrides: {
        containedError: ({ theme }) => ({
          '&:active': {
            background: theme.ui.button.contained.error.active.background,
          },
        }),
        containedForceWhite: ({ theme }) => ({
          '&:active': {
            background: theme.ui.button.contained.forceWhite.active.background,
          },
        }),
        containedInfo: ({ theme }) => ({
          '&:active': {
            background: theme.ui.button.contained.info.active.background,
          },
        }),
        containedPrimary: ({ theme }) => ({
          '&:active': {
            background: theme.ui.button.contained.primary.active.background,
          },
        }),
        containedSecondary: ({ theme }) => ({
          '&:active': {
            background: theme.ui.button.contained.secondary.active.background,
          },
        }),
        containedSuccess: ({ theme }) => ({
          '&:active': {
            background: theme.ui.button.contained.success.active.background,
          },
        }),
        containedWarning: ({ theme }) => ({
          '&:active': {
            background: theme.ui.button.contained.warning.active.background,
          },
        }),
        outlined: {
          '&.Mui-focusVisible': {
            '&::after': {
              inset: -2,
            },
          },
        },
        outlinedError: ({ theme }) => ({
          '&:active': {
            background: alpha(theme.palette.error.main, 0.1),
          },
        }),
        outlinedForceWhite: ({ theme }) => ({
          '&:active': {
            background: alpha(theme.palette.forceWhite.main, 0.1),
          },
        }),
        outlinedInfo: ({ theme }) => ({
          '&:active': {
            background: alpha(theme.palette.info.main, 0.1),
          },
        }),
        outlinedPrimary: ({ theme }) => ({
          '&:active': {
            background: alpha(theme.palette.primary.main, 0.1),
          },
        }),
        outlinedSecondary: ({ theme }) => ({
          color: theme.ui.button.color.neutral,
          '&:active': {
            background: alpha(theme.palette.secondary.main, 0.1),
          },
        }),
        outlinedSuccess: ({ theme }) => ({
          '&:active': {
            background: alpha(theme.palette.success.main, 0.1),
          },
        }),
        outlinedWarning: ({ theme }) => ({
          '&:active': {
            background: alpha(theme.palette.warning.main, 0.1),
          },
        }),
        root: ({ theme }) => ({
          '&.Mui-focusVisible': {
            borderRadius: `${theme.ui.focus.borderRadius}`,
            outline: `${theme.ui.focus.outlineWidth} solid ${theme.palette.action.focusOutline}`,
            outlineOffset: `${theme.ui.focus.outlineOffset}`,
            zIndex: 1, // Ensure the outline is above the shadow
          },
          textTransform: tokens.typography.button
            .textCase as CSSProperties['textTransform'],
        }),
        textError: ({ theme }) => ({
          '&:active': {
            background: alpha(theme.palette.error.main, 0.1),
          },
        }),
        textForceWhite: ({ theme }) => ({
          '&:active': {
            background: alpha(theme.palette.forceWhite.main, 0.1),
          },
        }),
        textInfo: ({ theme }) => ({
          '&:active': {
            background: alpha(theme.palette.info.main, 0.1),
          },
        }),
        textPrimary: ({ theme }) => ({
          '&:active': {
            background: alpha(theme.palette.primary.main, 0.1),
          },
        }),
        textSecondary: ({ theme }) => ({
          color: theme.ui.button.color.neutral,
          '&:active': {
            background: alpha(theme.palette.secondary.main, 0.1),
          },
        }),
        textSuccess: ({ theme }) => ({
          '&:active': {
            background: alpha(theme.palette.success.main, 0.1),
          },
        }),
        textWarning: ({ theme }) => ({
          '&:active': {
            background: alpha(theme.palette.warning.main, 0.1),
          },
        }),
      },
    },
  } as ThemeOptions['components'];
}
